import CloudIcon from "@mui/icons-material/Cloud";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Link, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import { alpha } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

const Page = () => {
  const { t } = useTranslation();
  usePageView();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const privacyFeatures = [
    {
      icon: SecurityIcon,
      title: t("NEN 7510 Certification"),
      description: t("CommitedDescription"),
      color: "#E3F2FD",
    },
    {
      icon: LockIcon,
      title: t("DiscretionConfidentiality"),
      description: t("DiscretionConfidentialityDescription"),
      color: "#F3E5F5",
    },
    {
      icon: VerifiedUserIcon,
      title: t("SaveDataSharing"),
      description: t("SaveDataSharingDescription"),
      color: "#E8F5E8",
    },
    {
      icon: CloudIcon,
      title: t("Automated Daily Backups"),
      description: t(
        "We perform automated daily backups of all Nightscout data, ensuring the continuous protection and availability of health data. All backup data is encrypted using industry-standard encryption algorithms with a secure encryption key, ensuring that your sensitive information remains protected both during transmission and while at rest in cloud storage."
      ),
      color: "#E3F2FD",
    },
  ];

  return (
    <>
      <Seo title={t("Data & Privacy")} />
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            pt: 10,
            mt: 2,
            mb: 4,
            color: theme.palette.primary.main,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                textAlign: "center",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  mt: 2,
                  mb: 2,
                  color: theme.palette.primary.main,
                }}
              >
                {t("Data & Privacy")}
              </Typography>{" "}
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", color: "text.secondary", mb: 5 }}
              >
                {t(
                  "Open-source diabetes management through automated insulin delivery systems."
                )}
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Features Section */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            pb: smDown ? 6 : 10,
          }}
        >
          <Container maxWidth="lg">
            {privacyFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card
                  key={index}
                  sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    border: "1px solid #F3F4F6",
                    overflow: "hidden",
                    mt: index > 0 ? 4 : 0,
                    height: { xs: "auto", md: "auto" },
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 4, md: 6 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Grid
                      container
                      spacing={4}
                      alignItems="center"
                      sx={{ height: "100%" }}
                    >
                      <Grid xs={12} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: { xs: 3, md: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: alpha(feature.color, 0.3),
                              borderRadius: "50%",
                              p: 3,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: 60,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid xs={12} md={9}>
                        <Typography
                          variant={smDown ? "h5" : "h4"}
                          sx={{
                            fontWeight: "bold",
                            mb: 2,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.8,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: feature.description,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.5),
            py: smDown ? 6 : 10,
            color: "white",
            mt: 6,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant={smDown ? "h4" : "h3"}
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  color: "white",
                }}
              >
                Questions About Your Privacy?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  maxWidth: "600px",
                  mx: "auto",
                  opacity: 0.6,
                  lineHeight: 1.7,
                }}
              >
                Contact us via{" "}
                <Link
                  href="https://wa.me/31614842402"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "white", textDecoration: "underline" }}
                >
                  WhatsApp
                </Link>{" "}
                or{" "}
                <Link
                  href="mailto:info@opensource.clinic?subject=Data%20Privacy%20Question"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "white", textDecoration: "underline" }}
                >
                  info@opensource.clinic
                </Link>
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Page;
